import { useCallback, useMemo } from "react";
import { useBoardRolesFactory } from "./useBoardRoles";

export interface BoardPermissions {
  canAccessBoard: boolean;
  canCreateTasks: boolean;
  canManageBoardGeneralSettings: boolean;
  canManageBoardAttributes: boolean;
  canManageFieldsVisibility: boolean;
  canManageColumnsOrder: boolean;
}

const DEFAULT_BOARD_PERMISSIONS: BoardPermissions = {
  canAccessBoard: false,
  canCreateTasks: false,
  canManageBoardGeneralSettings: false,
  canManageBoardAttributes: false,
  canManageFieldsVisibility: false,
  canManageColumnsOrder: false,
};

interface UseBoardPermissionsFactoryOptions {
  entityId: string;
  meId: string | undefined;
}

export const useBoardPermissionsFactory = ({
  entityId,
  meId,
}: UseBoardPermissionsFactoryOptions) => {
  const getBoardRoles = useBoardRolesFactory({ entityId, meId });

  const getBoardPermissions = useCallback(
    (boardId: string): BoardPermissions => {
      const { isBoardAdmin, isBoardMember, isBoardViewer } =
        getBoardRoles(boardId);
      return {
        canAccessBoard: isBoardAdmin || isBoardMember || isBoardViewer,
        canCreateTasks: isBoardAdmin || isBoardMember,
        canManageBoardGeneralSettings: isBoardAdmin,
        canManageBoardAttributes: isBoardAdmin || isBoardMember,
        canManageFieldsVisibility: isBoardAdmin || isBoardMember,
        canManageColumnsOrder: isBoardAdmin || isBoardMember || !boardId,
      };
    },
    [getBoardRoles]
  );

  return getBoardPermissions;
};

interface UseBoardPermissionsOptions extends UseBoardPermissionsFactoryOptions {
  boardId: string | undefined | null;
}

export const useBoardPermissions = (options: UseBoardPermissionsOptions) => {
  const getBoardPermissions = useBoardPermissionsFactory(options);

  const boardPermissions = useMemo(
    () =>
      options.boardId
        ? getBoardPermissions(options.boardId)
        : DEFAULT_BOARD_PERMISSIONS,
    [getBoardPermissions, options.boardId]
  );

  return boardPermissions;
};
